/**
 * ReferralLinkBox — Referral link display with copy button and share row.
 *
 * Layout:
 *   Section heading
 *   Link display row (icon + URL text + copy button)
 *   Share buttons row (Email | SMS | QR Code)
 *
 * Copy button shows checkmark for 1500ms after tap (visual feedback).
 * Wrapped in GlassCard withChromeBorder={false}.
 *
 * WCAG AA: All purple text uses #7C3AED (5.9:1 on white). Touch targets ≥ 44pt.
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { GlassCard } from '../../glass/GlassCard';

// ---------------------------------------------------------------------------
// Module-level constants
// ---------------------------------------------------------------------------

const LINK_BOX_BG = '#F4F4F5';
const LINK_BOX_BORDER = '#E4E4E7';
const COPY_BUTTON_BG = '#7C3AED';
const URL_TEXT_COLOR = '#7C3AED';
const LINK_ICON_COLOR = '#7C3AED';
const SHARE_LABEL_COLOR = '#71717A';
const SHARE_BORDER_COLOR = '#E4E4E7';

const EMAIL_ICON_COLOR = '#3B82F6';
const SMS_ICON_COLOR = '#10B981';
const QR_ICON_COLOR = '#7C3AED';

const COPY_FEEDBACK_DURATION_MS = 1500;

// ---------------------------------------------------------------------------
// Share button sub-component
// ---------------------------------------------------------------------------

interface ShareButtonProps {
  icon: React.ComponentProps<typeof Ionicons>['name'];
  iconColor: string;
  label: string;
  onPress: () => void;
}

const ShareButton: React.FC<ShareButtonProps> = ({
  icon,
  iconColor,
  label,
  onPress,
}) => (
  <Pressable
    onPress={onPress}
    style={styles.shareButton}
    accessibilityRole="button"
    accessibilityLabel={label}
  >
    <Ionicons name={icon} size={20} color={iconColor} />
    <Text style={styles.shareButtonLabel}>{label}</Text>
  </Pressable>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ReferralLinkBoxProps {
  shortLink: string;
  onCopy: () => void;
  onShareEmail: () => void;
  onShareSMS: () => void;
  onShowQR: () => void;
  /** i18n labels */
  title: string;
  copyAccessibilityLabel: string;
  shareEmailLabel: string;
  shareSMSLabel: string;
  shareQRLabel: string;
}

export const ReferralLinkBox: React.FC<ReferralLinkBoxProps> = ({
  shortLink,
  onCopy,
  onShareEmail,
  onShareSMS,
  onShowQR,
  title,
  copyAccessibilityLabel,
  shareEmailLabel,
  shareSMSLabel,
  shareQRLabel,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(() => {
    onCopy();
    setIsCopied(true);

    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
    }
    resetTimerRef.current = setTimeout(() => {
      setIsCopied(false);
      resetTimerRef.current = null;
    }, COPY_FEEDBACK_DURATION_MS);
  }, [onCopy]);

  return (
    <GlassCard withChromeBorder={false}>
      {/* Section heading */}
      <Text style={styles.sectionTitle}>{title}</Text>

      {/* Link display row */}
      <View style={styles.linkRow}>
        <View style={styles.linkBox}>
          <Ionicons name="link-outline" size={14} color={LINK_ICON_COLOR} />
          <Text style={styles.linkText} numberOfLines={1} ellipsizeMode="tail">
            {shortLink}
          </Text>
        </View>

        {/* Copy button */}
        <Pressable
          onPress={handleCopy}
          style={styles.copyButton}
          accessibilityRole="button"
          accessibilityLabel={copyAccessibilityLabel}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        >
          <Ionicons
            name={isCopied ? 'checkmark-outline' : 'copy-outline'}
            size={18}
            color="#FFFFFF"
          />
        </Pressable>
      </View>

      {/* Share buttons row */}
      <View style={styles.shareRow}>
        <ShareButton
          icon="mail-outline"
          iconColor={EMAIL_ICON_COLOR}
          label={shareEmailLabel}
          onPress={onShareEmail}
        />
        <ShareButton
          icon="chatbubble-outline"
          iconColor={SMS_ICON_COLOR}
          label={shareSMSLabel}
          onPress={onShareSMS}
        />
        <ShareButton
          icon="qr-code-outline"
          iconColor={QR_ICON_COLOR}
          label={shareQRLabel}
          onPress={onShowQR}
        />
      </View>
    </GlassCard>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#18181B',
    marginBottom: 12,
  } as TextStyle,
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  } as ViewStyle,
  linkBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: LINK_BOX_BG,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: LINK_BOX_BORDER,
    paddingVertical: 12,
    paddingHorizontal: 14,
    gap: 6,
  } as ViewStyle,
  linkText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: URL_TEXT_COLOR,
  } as TextStyle,
  copyButton: {
    width: 44,
    height: 44,
    backgroundColor: COPY_BUTTON_BG,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  } as ViewStyle,
  shareRow: {
    flexDirection: 'row',
    gap: 8,
  } as ViewStyle,
  shareButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: SHARE_BORDER_COLOR,
    borderRadius: 10,
    paddingVertical: 12,
    minHeight: 44,
    backgroundColor: '#FFFFFF',
    gap: 4,
  } as ViewStyle,
  shareButtonLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: SHARE_LABEL_COLOR,
  } as TextStyle,
});
